import { useMemo } from 'react';

import type { UsageSelection } from '../services/funnel-rehydrate.service';
import { useFunnel } from '../state/FunnelProvider';

/**
 * The shopper's live usage choice, in the shape the path builders take.
 *
 * ── WHY A HOOK AND NOT A CONDITIONAL SPREAD AT EACH CALL SITE ──────────────
 * Every funnel link has to carry the chosen kWh (`buildShelfPath`,
 * `buildPlanDetailPath`, `buildPlanStepPath`), and `UsageSelection`'s fields are
 * OPTIONAL while `FunnelState`'s are `| undefined` — so each call site was
 * re-writing the same `exactOptionalPropertyTypes` conditional spread. One hook
 * means a link cannot accidentally ship `{ monthlyUsageKwh: undefined }` (which
 * `usageSearchParams` treats as "no usage" anyway, but only by luck).
 *
 * ⚠️ It reads `FunnelProvider`, never the URL. The URL is the source of truth, but
 * the provider is the READ API for it (seeded by `FunnelHydrationGate` via
 * `buildFunnelSeed`); a component that parsed `useSearchParams()` itself would see
 * a different answer the moment the shopper changed usage without a navigation.
 */
export function useUsageSelection(): UsageSelection {
  const { monthlyUsageKwh, usageTier } = useFunnel();

  return useMemo<UsageSelection>(
    () => ({
      ...(monthlyUsageKwh === undefined ? {} : { monthlyUsageKwh }),
      ...(usageTier === undefined ? {} : { usageTier }),
    }),
    [monthlyUsageKwh, usageTier],
  );
}
